import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Plus, RotateCcw } from 'lucide-react';
import * as dpService from 'features/dp/services/dpService';

const inputClass =
  'w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] focus:outline-none focus:ring-2 focus:ring-teal-500';

function formatarData(iso) {
  if (!iso) return '—';
  const [a, m, d] = String(iso).substring(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

export default function AfastamentosTab({ podeEditar }) {
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [funcionarios, setFuncionarios] = useState([]);

  const [form, setForm] = useState({
    funcionario_id: '', tipo: 'doenca', data_inicio: '',
    data_fim_prevista: '', motivo: '',
  });

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await dpService.listarAfastamentos({ per_page: 50 });
      setRegistros(res.data?.data?.data ?? res.data?.data ?? []);
    } catch {
      setErro('Erro ao carregar afastamentos');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    dpService.listarFuncionarios({ status: 'ativo', per_page: 200 })
      .then(res => setFuncionarios(res.data?.data?.data ?? res.data?.data ?? []))
      .catch(() => {});
  }, []);

  const setField = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErro(null);
    try {
      await dpService.criarAfastamento(form);
      setMostrarForm(false);
      setForm({ funcionario_id: '', tipo: 'doenca', data_inicio: '', data_fim_prevista: '', motivo: '' });
      await carregar();
    } catch (err) {
      setErro(err?.response?.data?.message ?? 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const retornar = async (id) => {
    if (!window.confirm('Registrar retorno deste afastamento?')) return;
    try {
      await dpService.registrarRetorno(id, { data_retorno: new Date().toISOString().substring(0, 10) });
      await carregar();
    } catch (err) {
      setErro(err?.response?.data?.message ?? 'Erro ao registrar retorno');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle size={20} className="text-teal-600" />
          <h2 className="text-base font-bold text-[color:var(--text)]">Afastamentos</h2>
        </div>
        {podeEditar && (
          <button onClick={() => setMostrarForm(v => !v)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 transition-colors">
            <Plus size={14} /> Novo Afastamento
          </button>
        )}
      </div>

      {erro && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-xl">{erro}</div>}

      {mostrarForm && (
        <form onSubmit={handleSubmit} className="border border-[color:var(--border)] rounded-2xl p-5 space-y-4 bg-[color:var(--surface-muted)]">
          <h3 className="text-sm font-semibold text-[color:var(--text)]">Novo Afastamento</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Funcionário *</label><select required value={form.funcionario_id} onChange={setField('funcionario_id')} className={inputClass}><option value="">Selecione</option>{funcionarios.map(f => <option key={f.id} value={f.id}>{f.pessoa?.nome}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Tipo *</label><select required value={form.tipo} onChange={setField('tipo')} className={inputClass}><option value="doenca">Doença</option><option value="acidente_trabalho">Acidente de Trabalho</option><option value="licenca_maternidade">Licença Maternidade</option><option value="licenca_paternidade">Licença Paternidade</option><option value="servico_militar">Serviço Militar</option><option value="outros">Outros</option></select></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Data Início *</label><input type="date" required value={form.data_inicio} onChange={setField('data_inicio')} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Previsão Fim</label><input type="date" value={form.data_fim_prevista} onChange={setField('data_fim_prevista')} className={inputClass} /></div>
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Motivo</label><input value={form.motivo} onChange={setField('motivo')} className={inputClass} /></div>
          </div>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => setMostrarForm(false)} className="px-4 py-2 text-sm rounded-xl border border-[color:var(--border)] text-[color:var(--text-muted)]">Cancelar</button>
            <button type="submit" disabled={salvando} className="px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60">{salvando ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </form>
      )}

      {carregando ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full" /></div>
      ) : registros.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertTriangle size={40} className="text-[color:var(--text-muted)] opacity-30 mb-3" />
          <p className="text-sm text-[color:var(--text-muted)]">Nenhum afastamento encontrado.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface-muted)]">
              <tr>
                {['Funcionário', 'Tipo', 'Início', 'Prev. Fim', 'Retorno', 'Status', 'Ações'].map(col => (
                  <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-[color:var(--text-muted)] whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border)]">
              {registros.map(r => (
                <tr key={r.id} className="hover:bg-[color:var(--surface-muted)] transition-colors">
                  <td className="px-4 py-3 font-medium text-[color:var(--text)]">{r.funcionario?.pessoa?.nome ?? '—'}</td>
                  <td className="px-4 py-3 text-[color:var(--text-muted)] capitalize">{(r.tipo || '').replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 text-[color:var(--text-muted)]">{formatarData(r.data_inicio)}</td>
                  <td className="px-4 py-3 text-[color:var(--text-muted)]">{formatarData(r.data_fim_prevista)}</td>
                  <td className="px-4 py-3 text-[color:var(--text-muted)]">{formatarData(r.data_retorno)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${r.data_retorno ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {r.data_retorno ? 'Retornado' : 'Afastado'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {podeEditar && !r.data_retorno && (
                      <button onClick={() => retornar(r.id)} title="Registrar retorno"
                        className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                        <RotateCcw size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
